'use client'
import { ListItem } from '@/components/list-item'
import { Logo } from '@/components/logo'
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from '@/components/navigation-menu'
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { config } from '@/lib/config'
import type { SiteCopy } from '@/lib/site-copy'
import { cn } from '@/lib/utils'
import { DotPattern } from '@tailark/core/magicui/dot-pattern'
import { FeatureIconFrame } from '@tailark/core/marketing'
import { Button } from '@tailark/core/ui/button'
import {
    type LucideIcon,
    BookOpen,
    FileText,
    Handshake,
    HelpCircle,
    Layers,
    LayoutTemplate,
    Menu,
    MonitorSmartphone,
    ScrollText,
    SquarePen,
    UserRoundPlus,
    X,
} from 'lucide-react'
import Link from 'next/link'
import React from 'react'

/** Neutral tile; on row hover, diagonal wash bottom → north-east (transparent). */
const NAV_MENU_ICON_FRAME_CLASS = cn(
    'relative overflow-hidden',
    'transition-[background-image,background-color,color,box-shadow] duration-200 ease-out',
)
const NAV_MENU_ICON_CLASS =
    'size-5 transition-colors group-hover:text-primary/85'

export const HeroHeader = ({ translations }: { translations: SiteCopy }) => {
    const t = translations
    const { loginUrl, signUpUrl, redirectUrl } = config
    const dir = 'rtl' as const

    const menuItems = [
        { name: t.nav.solution, href: '/', hasDropdown: true },
        { name: t.nav.features, href: '/features' },
        { name: t.nav.pricing, href: '/pricing' },
        { name: t.nav.docs, href: '/docs', hasDocs: true },
        { name: t.nav.templates, href: '/templates' },
    ]

    /** Six pillars aligned with docs/ai-website-content (verticals + templates, capabilities hub, embed). */
    const solutionItems: {
        href: string
        title: string
        description: string
        Lucide: LucideIcon
    }[] = [
            {
                Lucide: SquarePen,
                title: t.solutions.formBuilder.title,
                href: '/form-builder',
                description: t.solutions.formBuilder.description,
            },
            {
                Lucide: Handshake,
                title: t.solutions.salesDealRoom.title,
                href: '/sales-deal-room',
                description: t.solutions.salesDealRoom.description,
            },
            {
                Lucide: UserRoundPlus,
                title: t.solutions.customerOnboarding.title,
                href: '/customer-onboarding',
                description: t.solutions.customerOnboarding.description,
            },
            {
                Lucide: LayoutTemplate,
                title: t.solutions.templatesQuick.title,
                href: '/templates',
                description: t.solutions.templatesQuick.description,
            },
            {
                Lucide: Layers,
                title: t.solutions.capabilitiesHub.title,
                href: '/features',
                description: t.solutions.capabilitiesHub.description,
            },
            {
                Lucide: MonitorSmartphone,
                title: t.solutions.embedOnSite.title,
                href: '/docs/embed',
                description: t.solutions.embedOnSite.description,
            },
        ]

    const docsItems: {
        name: string
        href: string
        description: string
        icon: LucideIcon
    }[] = [
            {
                name: t.docs.caseStudies,
                href: '/docs',
                description: t.docs.caseStudiesDesc,
                icon: FileText,
            },
            {
                name: t.docs.tutorials,
                href: '/docs/tutorials',
                description: t.docs.tutorialsDesc,
                icon: BookOpen,
            },
            {
                name: t.docs.guides,
                href: '/docs/guides',
                description: t.docs.guidesDesc,
                icon: ScrollText,
            },
            {
                name: t.docs.faq,
                href: '/docs/faq',
                description: t.docs.faqDesc,
                icon: HelpCircle,
            },
        ]
    const [menuState, setMenuState] = React.useState(false)
    const [isScrolled, setIsScrolled] = React.useState(false)

    React.useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])
    return (
        <header>
            <nav
                data-state={menuState && 'active'}
                className="fixed z-20 w-full px-2">
                <div className={cn('mx-auto mt-2 max-w-6xl px-6 transition-all duration-300 lg:px-12 border border-transparent', isScrolled && 'bg-background/50 max-w-5xl rounded-md md:rounded-2xl border-border backdrop-blur-lg lg:px-5')}>
                    <div className="relative flex flex-wrap items-center justify-between gap-6 py-3 lg:gap-0 lg:py-4">
                        <div className="flex w-full lg:w-auto gap-4">
                            <Sheet open={menuState} onOpenChange={setMenuState}>
                                <SheetTrigger asChild>
                                    <button
                                        type="button"
                                        aria-expanded={menuState}
                                        aria-haspopup="dialog"
                                        aria-label={menuState ? 'Close menu' : 'Open menu'}
                                        className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden">
                                        <Menu className="in-data-[state=active]:rotate-180 in-data-[state=active]:scale-0 in-data-[state=active]:opacity-0 m-auto size-6 duration-200" />
                                        <X className="in-data-[state=active]:rotate-0 in-data-[state=active]:scale-100 in-data-[state=active]:opacity-100 absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 duration-200" />
                                    </button>
                                </SheetTrigger>
                                <SheetContent
                                    side="top"
                                    dir={dir}
                                    className="max-h-[min(92dvh,100dvh)] gap-0 overflow-y-auto overscroll-y-contain rounded-b-2xl border-x-0 px-4 pt-4">
                                    <SheetHeader className="sr-only shrink-0 border-0 p-0">
                                        <SheetTitle>Main menu</SheetTitle>
                                    </SheetHeader>
                                    <nav className="flex flex-col gap-8 pb-6" aria-label="Mobile">
                                        <ul className="space-y-1 text-lg">
                                            {menuItems.map((item, index) => (
                                                <li key={index}>
                                                    <SheetClose asChild>
                                                        <Link
                                                            href={item.href}
                                                            className="text-muted-foreground hover:text-accent-foreground block rounded-md px-3 py-2.5 duration-150">
                                                            <span>{item.name}</span>
                                                        </Link>
                                                    </SheetClose>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="flex w-full flex-col gap-3 sm:flex-row sm:gap-3">
                                            <SheetClose asChild>
                                                <Button asChild variant="outline" size="sm">
                                                    <a href={loginUrl}>
                                                        <span>{t.common.login}</span>
                                                    </a>
                                                </Button>
                                            </SheetClose>
                                            <SheetClose asChild>
                                                <Button asChild size="sm">
                                                    <a href={signUpUrl}>
                                                        <span>{t.common.signUp}</span>
                                                    </a>
                                                </Button>
                                            </SheetClose>
                                            <SheetClose asChild>
                                                <Button
                                                    asChild
                                                    size="sm"
                                                    className={cn(isScrolled ? 'inline-flex' : 'hidden')}
                                                >
                                                    <a href={redirectUrl}>
                                                        <span>{t.common.getStarted}</span>
                                                    </a>
                                                </Button>
                                            </SheetClose>
                                        </div>
                                    </nav>
                                </SheetContent>
                            </Sheet>
                            <Link
                                href="/"
                                aria-label="home"
                                className="relative z-20 flex items-center space-x-2">
                                <Logo />
                            </Link>


                        </div>

                        <div className="pointer-events-none absolute inset-x-0 top-1/2 z-[1] hidden w-full -translate-y-1/2 lg:block">
                            <NavigationMenu
                                dir={dir}
                                className="pointer-events-none relative z-10 w-full max-w-none justify-center">
                                <NavigationMenuList className="pointer-events-auto flex w-full flex-row justify-center gap-4">
                                    {menuItems.map((item, index) => (
                                        <NavigationMenuItem key={index}>
                                            {item.hasDropdown || item.hasDocs ? (
                                                <>
                                                    <NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground border-none bg-transparent shadow-none hover:bg-transparent">{item.name}</NavigationMenuTrigger>
                                                    <NavigationMenuContent className="!rounded-2xl p-3">
                                                        <ul
                                                            className={cn(
                                                                'grid gap-1.5',
                                                                item.hasDropdown &&
                                                                'md:w-[min(92vw,560px)] !rounded-2xl  lg:w-[min(92vw,760px)] xl:w-[820px] lg:grid-cols-[minmax(220px,260px)_minmax(0,1fr)_minmax(0,1fr)]', item.hasDocs && 'md:w-[400px] lg:w-[520px] lg:grid-cols-2',
                                                            )}>
                                                            <li
                                                                className="row-span-3"
                                                                hidden={item.hasDocs}>
                                                                <NavigationMenuLink asChild>
                                                                    <a
                                                                        className="relative ring-1 ring-border ring-offset-4  flex h-full w-full flex-col justify-end overflow-hidden rounded-xl bg-primary p-6 text-white no-underline shadow-md outline-none transition-[box-shadow,filter] duration-300 hover:brightness-[1.03] hover:shadow-lg focus-visible:ring-2 focus-visible:ring-sky-300/60"
                                                                        href="/sales-deal-room">
                                                                        <DotPattern className="absolute inset-0" glow={true} />
                                                                        <div className="mask-r-from-50% pointer-events-none absolute inset-0 [background:radial-gradient(150%_115%_at_50%_5%,transparent_25%,var(--color-teal-500)_60%,var(--color-white)_100%)]" aria-hidden />
                                                                        <div className="pointer-events-none absolute inset-0 bg-white/10" aria-hidden />
                                                                        <div className="relative mb-2 mt-4 text-lg font-medium drop-shadow-sm">{t.solutions.title}</div>
                                                                        <p className="relative text-sm leading-tight text-white/85 drop-shadow-sm">{t.solutions.description}</p>
                                                                    </a>
                                                                </NavigationMenuLink>
                                                            </li>
                                                            {item.hasDropdown &&
                                                                solutionItems.map((solutionItem, solutionIndex) => (
                                                                    <ListItem
                                                                        key={solutionIndex}
                                                                        compact
                                                                        href={solutionItem.href}
                                                                        title={solutionItem.title}
                                                                        leadingIcon={
                                                                            <FeatureIconFrame
                                                                                className={cn('size-12', NAV_MENU_ICON_FRAME_CLASS)}>
                                                                                <solutionItem.Lucide className={NAV_MENU_ICON_CLASS} aria-hidden />
                                                                            </FeatureIconFrame>
                                                                        }>
                                                                        {solutionItem.description}
                                                                    </ListItem>
                                                                ))}
                                                            {item.hasDocs &&
                                                                docsItems.map((docsItem, docsIndex) => (
                                                                    <ListItem
                                                                        key={docsIndex}
                                                                        compact
                                                                        href={docsItem.href}
                                                                        title={docsItem.name}
                                                                        leadingIcon={
                                                                            <FeatureIconFrame
                                                                                className={cn('size-12', NAV_MENU_ICON_FRAME_CLASS)}>
                                                                                <docsItem.icon className={NAV_MENU_ICON_CLASS} aria-hidden />
                                                                            </FeatureIconFrame>
                                                                        }>
                                                                        {docsItem.description}
                                                                    </ListItem>
                                                                ))}
                                                        </ul>
                                                    </NavigationMenuContent>
                                                </>
                                            ) : (
                                                <Button asChild variant="ghost" className="text-muted-foreground hover:text-accent-foreground font-normal">
                                                    <Link href={item.href}>
                                                        <span>{item.name}</span>
                                                    </Link>
                                                </Button>
                                            )}
                                        </NavigationMenuItem>
                                    ))}
                                </NavigationMenuList>
                            </NavigationMenu>
                        </div>

                        <div className="relative z-20 mb-6 hidden w-full flex-wrap items-center justify-end md:flex-nowrap lg:mb-0 lg:flex lg:w-fit lg:gap-6">
                            <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit">
                                <Button
                                    asChild
                                    variant="outline"
                                    size="sm"
                                    className={cn(isScrolled && 'lg:hidden')}>
                                    <a href={loginUrl}>
                                        <span>{t.common.login}</span>
                                    </a>
                                </Button>
                                <Button
                                    asChild
                                    size="sm"
                                    className={cn(isScrolled && 'lg:hidden')}>
                                    <a href={signUpUrl}>
                                        <span>{t.common.signUp}</span>
                                    </a>
                                </Button>
                                <Button
                                    asChild
                                    size="sm"
                                    className={cn(isScrolled ? 'lg:inline-flex' : 'hidden')}>
                                    <a href={redirectUrl}>
                                        <span>{t.common.getStarted}</span>
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    )
}
